import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button — `.btn` / `.btn-primary` / `.btn-sm` макета.
 * Все цвета/метрики из токенов; состояния hover/focus/active/disabled.
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-8',
    'transition-colors duration-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-panel',
    'active:translate-y-px',
    'disabled:pointer-events-none disabled:opacity-50',
  ),
  {
    variants: {
      variant: {
        primary: 'border border-accent bg-accent text-white hover:bg-accent-txt',
        secondary: 'border border-line-strong bg-white text-txt hover:bg-hover',
        // Опасное (деструктивное) действие — удаление и т.п.: красная заливка, hover — насыщённее.
        danger: 'border border-red bg-red text-white hover:bg-red-strong',
      },
      size: {
        sm: 'px-3 py-1.5 text-13',
        md: 'px-3.5 py-2 text-14',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Рендерить как дочерний элемент (Radix Slot) — например, ссылку-кнопку. */
  asChild?: boolean
  /** Иконка слева от подписи. */
  leftIcon?: ReactNode
  /** Иконка справа от подписи. */
  rightIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, leftIcon, rightIcon, children, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className)
    // asChild: Radix Slot требует РОВНО один дочерний элемент. Подмешивать иконки нельзя —
    // {leftIcon}{children}{rightIcon} даёт массив из трёх (даже при undefined-иконках), и
    // Slot падает «Expected a single React element child». Для кнопки-ссылки иконку, если
    // нужна, кладут внутрь дочернего элемента.
    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children}
        </Slot>
      )
    }
    return (
      <button ref={ref} className={classes} {...props}>
        {leftIcon}
        {children}
        {rightIcon}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
